import {
  AccountBalance as AccountBalanceIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { Box, ButtonBase, Typography } from '@mui/material';
import React from 'react';
import { RecentlyViewedItem } from 'RecentlyViewed';
import { Palette } from 'Palette';
import { RADIUS } from 'DesignTokens';
import { getModelDisplayName, getModelIcon } from './recentlyViewedSidebarUtils';

interface RecentlyViewedCompactCardsProps {
  items: RecentlyViewedItem[];
  tone: keyof typeof Palette;
  onItemClick: (item: RecentlyViewedItem) => void;
}

interface ChipInfo {
  name: string;
  subtitle: string;
  avatar: React.ReactNode;
}

const getInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

const getChipInfo = (item: RecentlyViewedItem): ChipInfo => {
  const data = item.modelData;

  if (item.modelType === 'CONTACT' && data) {
    const name = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unnamed Contact';
    return { name, subtitle: data.email || '', avatar: getInitials(name) };
  }

  if (item.modelType === 'INVESTOR' && data) {
    const { firstName, lastName, email } = data.contact || {};
    const name = firstName && lastName ? `${firstName} ${lastName}` : 'Unnamed Investor';
    return { name, subtitle: email || '', avatar: getInitials(name) };
  }

  if (item.modelType === 'FUND_OPPORTUNITY' && data) {
    return {
      name: data.name || 'Unnamed Opportunity',
      subtitle: 'Fund opportunity',
      avatar: <BusinessIcon sx={{ fontSize: 16 }} />,
    };
  }

  if (item.modelType === 'INVESTMENT_ENTITY' && data) {
    return {
      name: data.name || 'Unnamed Entity',
      subtitle: 'Investment entity',
      avatar: <AccountBalanceIcon sx={{ fontSize: 16 }} />,
    };
  }

  return {
    name: getModelDisplayName(item),
    subtitle: '',
    avatar: React.cloneElement(getModelIcon(item.modelType), { sx: { fontSize: 16 } }),
  };
};

export const RecentlyViewedCompactCards: React.FC<RecentlyViewedCompactCardsProps> = ({
  items,
  onItemClick,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: 1,
    }}
  >
    {items.map((item, index) => {
      const { name, subtitle, avatar } = getChipInfo(item);

      return (
        <ButtonBase
          key={`${item.modelType}-${item.modelId}-${index}`}
          aria-label={name}
          onClick={() => onItemClick(item)}
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 1.25,
            minWidth: 0,
            px: 1.25,
            py: 1,
            textAlign: 'left',
            backgroundColor: Palette.tone1.background,
            border: `1px solid ${Palette.tone1.border}`,
            borderRadius: RADIUS.sectionChild,
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            '&:hover, &:focus-visible': {
              borderColor: Palette.default.buttonBg,
              boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
            },
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: Palette.default.activeBg,
              color: Palette.default.activeText,
              fontSize: '0.6875rem',
              fontWeight: 700,
            }}
          >
            {avatar}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                lineHeight: 1.3,
                color: Palette.default.text,
              }}
            >
              {name}
            </Typography>
            {subtitle && (
              <Typography
                noWrap
                sx={{ fontSize: '0.75rem', lineHeight: 1.3, color: Palette.tone3.disabledText }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </ButtonBase>
      );
    })}
  </Box>
);
